import { Navigate } from "react-router-dom";
import { useEffect, useMemo, useState } from "react";
import { useFiscalStore } from "@/store/fiscal-store";
import { fmtBRL, fmtNum, fmtDur } from "@/lib/format";
import { cn } from "@/lib/utils";
import {
  AlertTriangle, CheckCircle2, Fingerprint, Hash, Search, ShieldAlert, ShieldCheck, Sigma, Layers, Clock, ArrowRight, Zap, Target, Cpu, Database, Activity, History, Loader2, Gauge, AlertCircle
} from "lucide-react";
import type { AuditCategory, AuditSeverity } from "@/lib/fiscal-types";
import { Button } from "@/components/ui/button";

interface RuleSpec {
  code: string; layer: 1 | 2 | 3 | 4; category: AuditCategory; title: string; desc: string; severity: AuditSeverity; status: "ATIVA" | "PENDENTE_BACKEND";
}

const RULES: RuleSpec[] = [
  { code: "R1",  layer: 1, category: "INTEGRIDADE", title: "Hash SHA-256 do XML original", desc: "Cada XML recebe um hash SHA-256 imutável. Duplicatas exatas (byte a byte) são ignoradas automaticamente.", severity: "INFO",   status: "ATIVA" },
  { code: "R4",  layer: 2, category: "MATEMATICA",  title: "vProd = qCom × vUnCom", desc: "Para cada item, valida que o produto da quantidade pela unitário bate com vProd (aritmética em centavos).", severity: "MEDIA",   status: "ATIVA" },
  { code: "R5",  layer: 2, category: "MATEMATICA",  title: "Totais ICMSTot vs soma dos itens", desc: "vProd, vICMS, vST, vPIS, vCOFINS, vIPI declarados batem com a soma item-a-item, com tolerância dinâmica.", severity: "MEDIA",   status: "ATIVA" },
  { code: "R6",  layer: 2, category: "MATEMATICA",  title: "Fórmula oficial do vNF", desc: "vNF = vProd + vFrete + vSeg + vOutro + vII + vIPI + vST + vFCPST − vDesc − vICMSDeson.", severity: "ALTA",    status: "ATIVA" },
  { code: "R7",  layer: 3, category: "TRIBUTARIA",  title: "CST × valores ICMS", desc: "CST 00/10/20/70/90 exigem vBC, pICMS, vICMS > 0. CST 40/41/50/51 exigem vICMS = 0.", severity: "ALTA",    status: "ATIVA" },
  { code: "R8",  layer: 3, category: "TRIBUTARIA",  title: "CFOP × tipo de operação (tpNF)", desc: "CFOPs 1/2/3 são entrada (tpNF=0); 5/6/7 são saída (tpNF=1).", severity: "MEDIA",   status: "ATIVA" },
  { code: "R9",  layer: 3, category: "PROTOCOLO",   title: "Ambiente vs protocolo", desc: "tpAmb=1 (produção) exige nProt e dhRecbto. tpAmb=2 = HOMOLOGAÇÃO (sem valor fiscal).", severity: "CRITICA", status: "ATIVA" },
  { code: "R10", layer: 3, category: "INTEGRIDADE", title: "Duplicidade por chave de acesso", desc: "Detecta XMLs distintos com a mesma chave de 44 dígitos (re-emissão suspeita).", severity: "ALTA",    status: "ATIVA" },
  { code: "R11", layer: 4, category: "COERENCIA",   title: "Valor alto com imposto zero", desc: "vNF > R$ 10.000 com ICMS = 0 e sem CST de isenção é sinalizado para verificação técnica.", severity: "MEDIA",   status: "ATIVA" },
  { code: "R13", layer: 4, category: "COERENCIA",   title: "Coerência de transporte", desc: "modFrete ≠ 9 (sem transporte) exige preenchimento do grupo <transporta>.", severity: "MEDIA",   status: "ATIVA" },
  { code: "R14", layer: 4, category: "COERENCIA",   title: "Soma de pagamentos vs vNF", desc: "Σ vPag − vTroco = vNF. Detecta divergências entre <pag> e <total>.", severity: "ALTA",    status: "ATIVA" },
  { code: "R15", layer: 4, category: "TRIBUTARIA",  title: "IBS/CBS (Reforma Tributária)", desc: "vIBS = vIBSUF + vIBSMun e vCBS calculados conforme schema da reforma.", severity: "ALTA",    status: "ATIVA" },
];

const SEV_STYLES: Record<AuditSeverity, { bg: string; text: string; border: string; label: string }> = {
  CRITICA: { bg: "bg-red-500/10", text: "text-red-500", border: "border-red-500/20", label: "BLOCKER" },
  ALTA:    { bg: "bg-orange-500/10", text: "text-orange-500", border: "border-orange-500/20", label: "HIGH" },
  MEDIA:   { bg: "bg-amber-500/10", text: "text-amber-500", border: "border-amber-500/20", label: "MEDIUM" },
  BAIXA:   { bg: "bg-yellow-500/10", text: "text-yellow-500", border: "border-yellow-500/20", label: "LOW" },
  INFO:    { bg: "bg-sky-500/10", text: "text-sky-500", border: "border-sky-500/20", label: "TRACE" },
};

function SeverityBadge({ s }: { s: AuditSeverity }) {
  const sty = SEV_STYLES[s];
  return (
    <span className={cn(`px-3 py-1 rounded-lg text-[9px] font-black tracking-widest border uppercase shadow-lg`, sty.bg, sty.text, sty.border)}>
      {sty.label}
    </span>
  );
}

export default function InspecaoPage() {
  const { dashboard, notas, duracaoMs } = useFiscalStore();
  const [filtroRegra, setFiltroRegra] = useState<string | null>(null);
  const [busca, setBusca] = useState("");

  useEffect(() => { document.title = "Inspection Engine — DataGrid XML"; }, []);

  const audit = dashboard?.auditSummary;
  const notasComFindings = useMemo(() => notas.filter((n) => (n.auditFindings?.length ?? 0) > 0), [notas]);
  const notasFiltradas = useMemo(() => {
    let arr = notasComFindings;
    if (filtroRegra) arr = arr.filter((n) => n.auditFindings?.some((f) => f.regra === filtroRegra));
    if (busca.trim()) {
      const q = busca.trim().toLowerCase();
      arr = arr.filter((n) => n.chave.toLowerCase().includes(q) || n.numero.toLowerCase().includes(q) || n.emitente.toLowerCase().includes(q));
    }
    return arr.slice(0, 100);
  }, [notasComFindings, filtroRegra, busca]);

  // Removido redirecionamento automático para permitir acesso livre conforme solicitado.
  // if (!dashboard && notas.length === 0) return <Navigate to="/upload" replace />;

  return (
    <div className="p-4 lg:p-6 max-w-[1600px] mx-auto space-y-6 animate-in fade-in duration-1000">
      
      {/* High-End Forensic Header */}
      <header className="flex flex-col lg:flex-row lg:items-center justify-between gap-6 border-l-4 border-primary pl-8 py-4 mb-10 bg-white/[0.02] glass-technical border-y-0 border-r-0 rounded-r-3xl">
        <div>
          <div className="flex items-center gap-2 mb-2">
            <Zap className="size-3 text-primary animate-pulse" />
            <span className="text-[10px] font-black uppercase tracking-[0.4em] text-primary">Structural Integrity Analysis</span>
          </div>
          <h1 className="text-5xl font-black tracking-tighter leading-none mb-2">
            Inspection <span className="text-primary">Engine</span>
          </h1>
          <p className="text-[12px] text-muted-foreground font-black uppercase tracking-[0.1em] mt-2 flex items-center gap-6 opacity-70">
            <span className="flex items-center gap-2"><Fingerprint className="size-3.5 text-emerald-500" /> SHA-256 PROTOCOL ACTIVE</span>
            <span className="flex items-center gap-2"><History className="size-3.5" /> ENGINE VERSION: v4.1.0-STABLE</span>
          </p>
        </div>

        <div className="flex items-center gap-4">
          <div className="glass-technical px-6 py-3 border-emerald-500/20 bg-emerald-500/[0.03] flex flex-col justify-center">
             <span className="text-[8px] font-black text-emerald-500 uppercase tracking-widest leading-none mb-1">Data Health Score</span>
             <div className="flex items-center gap-3">
                <span className="text-2xl font-black text-emerald-600 tabular-nums leading-none tracking-tighter">98.4<span className="text-xs opacity-40">%</span></span>
                <div className="h-1.5 w-16 bg-emerald-500/10 rounded-full overflow-hidden">
                   <div className="h-full bg-emerald-500" style={{ width: '98.4%' }} />
                </div>
             </div>
          </div>
        </div>
      </header>

      {/* Forensic Stats Bento Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
        {[
          { label: "Documentos Íntegros", value: fmtNum(notas.length), sub: "Byte-to-Byte Verified", icon: CheckCircle2, color: "text-emerald-500", delay: 100 },
          { label: "Anomalias Detectadas", value: fmtNum(audit?.totalFindings ?? 0), sub: `Em ${fmtNum(audit?.notasComAchados ?? 0)} Entidades`, icon: ShieldAlert, color: "text-red-500", delay: 200 },
          { label: "Deduplicações", value: fmtNum(audit?.duplicatasIgnoradas ?? 0), sub: "Eliminação via Hash SHA-256", icon: Hash, color: "text-amber-500", delay: 300 },
          { label: "Throughput Engine", value: `${(notas.length / Math.max(1, duracaoMs / 1000)).toFixed(0)}`, sub: "Arquivos Processados / Segundo", icon: Zap, color: "text-primary", delay: 400 },
        ].map((s, i) => (
          <div key={s.label} className="glass-card p-6 group hover:border-primary/20 transition-all duration-500 animate-in slide-in-from-bottom duration-700" style={{ animationDelay: `${s.delay}ms` }}>
            <div className="flex items-center justify-between mb-4">
              <div className={cn("size-10 rounded-xl glass border-foreground/5 flex items-center justify-center group-hover:rotate-6 transition-transform", s.color)}>
                <s.icon className="size-5" />
              </div>
              <Activity className="size-4 text-muted-foreground/10 group-hover:text-primary/40 transition-colors" />
            </div>
            <div className="text-[9px] font-black uppercase tracking-[0.2em] text-muted-foreground mb-1">{s.label}</div>
            <div className="text-3xl font-black tabular-nums tracking-tighter leading-none mb-2">{s.value}</div>
            <p className="text-[10px] font-bold text-muted-foreground/60 uppercase tracking-tight">{s.sub}</p>
          </div>
        ))}
      </div>

      {/* Severity Matrix Hub */}
      <div className="glass-card p-8 border-primary/5 bg-white/[0.01]">
        <div className="flex items-center justify-between mb-8">
           <div>
              <h2 className="text-sm font-black uppercase tracking-[0.3em] flex items-center gap-2">
                <Layers className="size-4 text-primary" /> Incident Severity Distribution
              </h2>
              <p className="text-[9px] font-black text-muted-foreground uppercase tracking-widest mt-1">Classificação por Impacto na Integridade</p>
           </div>
           <div className="px-4 py-2 rounded-xl glass-technical border-foreground/5 text-[9px] font-black uppercase tracking-widest text-muted-foreground">
              Total findings: {fmtNum(audit?.totalFindings ?? 0)}
           </div>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-5 gap-6">
          {(["CRITICA", "ALTA", "MEDIA", "BAIXA", "INFO"] as AuditSeverity[]).map((s) => {
            const sty = SEV_STYLES[s];
            const n = audit?.porSeveridade[s] ?? 0;
            return (
              <div key={s} className={cn(`p-6 rounded-[2rem] flex flex-col justify-between transition-all duration-500 hover:scale-[1.05] border-2 glass-technical relative overflow-hidden group`, sty.bg, sty.border)}>
                <div className="absolute top-0 left-0 w-full h-1 bg-white/10 opacity-0 group-hover:opacity-100 animate-scan transition-opacity" />
                <span className={cn(`text-[10px] font-black tracking-[0.25em] uppercase`, sty.text)}>{sty.label}</span>
                <div className="flex items-baseline gap-2 mt-4">
                   <span className="text-4xl font-black tabular-nums tracking-tighter leading-none">{fmtNum(n)}</span>
                   <span className="text-[10px] font-black opacity-30 tabular-nums">/{fmtNum(audit?.totalFindings ?? 0)}</span>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Rules Engine and Forensic Logs */}
      <div className="grid lg:grid-cols-12 gap-6">
        
        {/* Rules Scanner Panel */}
        <div className="glass-card p-8 lg:col-span-4 flex flex-col h-[850px] border-primary/5 bg-white/[0.01]">
          <div className="flex items-center justify-between mb-8">
            <div>
               <h2 className="text-sm font-black uppercase tracking-[0.3em] flex items-center gap-2">
                 <Target className="size-4 text-emerald-500" /> Active Scanner Hub
               </h2>
               <p className="text-[9px] font-black text-muted-foreground uppercase tracking-widest mt-1">Lógica de Inspeção Fiscal</p>
            </div>
            {filtroRegra && (
              <Button variant="ghost" onClick={() => setFiltroRegra(null)} className="h-8 px-4 text-[9px] font-black text-primary uppercase bg-primary/5 hover:bg-primary/10 rounded-xl tracking-widest">
                 RESET FILTER
              </Button>
            )}
          </div>
          
          <div className="space-y-3 flex-1 overflow-y-auto pr-3 custom-scrollbar">
            {RULES.map((r) => {
              const count = audit?.porRegra?.[r.code] ?? 0;
              const selected = filtroRegra === r.code;
              return (
                <div key={r.code} onClick={() => count > 0 && setFiltroRegra(selected ? null : r.code)}
                  className={cn(
                    "p-5 rounded-3xl border-2 transition-all duration-500 group relative overflow-hidden",
                    count > 0 ? 'cursor-pointer hover:border-primary/40' : 'cursor-not-allowed opacity-40',
                    selected ? "bg-primary/[0.03] border-primary shadow-2xl" : "glass border-foreground/[0.03]"
                  )}>
                  <div className="flex justify-between items-start mb-3 relative z-10">
                    <span className="text-[10px] font-black text-primary uppercase tracking-[0.2em] bg-primary/10 px-3 py-1.5 rounded-xl">
                      {r.code} <span className="opacity-40 mx-1">·</span> L{r.layer}
                    </span>
                    <div className="flex flex-col items-end">
                       <span className={cn("text-[10px] font-black uppercase tracking-[0.25em]", count > 0 ? "text-orange-500 animate-pulse" : "text-emerald-500")}>
                         {count > 0 ? `${fmtNum(count)} ISSUES` : "SYSTEM OK"}
                       </span>
                    </div>
                  </div>
                  <div className="text-sm font-black mb-2 tracking-tight group-hover:text-primary transition-colors relative z-10">{r.title}</div>
                  <p className="text-[10px] text-muted-foreground/70 leading-relaxed font-bold uppercase tracking-tight relative z-10">{r.desc}</p>
                </div>
              );
            })}
          </div>
        </div>

        {/* Forensic Incident History */}
        <div className="lg:col-span-8 glass-card p-8 flex flex-col h-[850px] border-primary/5">
          
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-6 mb-10">
            <div>
               <h2 className="text-sm font-black uppercase tracking-[0.3em] flex items-center gap-2">
                 <AlertTriangle className="size-4 text-orange-500 animate-bounce" /> Forensic Inspection Log
               </h2>
               <p className="text-[9px] font-black text-muted-foreground uppercase tracking-widest mt-1">Divergências e Alertas Identificados</p>
            </div>
            <div className="relative flex-1 group max-w-md">
              <Search className="size-4 absolute left-5 top-1/2 -translate-y-1/2 text-muted-foreground group-focus-within:text-primary transition-colors" />
              <input 
                value={busca} 
                onChange={(e) => setBusca(e.target.value)} 
                placeholder="Filtrar por Chave, Número ou Emitente..." 
                className="input-premium w-full pl-14 h-14 rounded-2xl bg-white/5 border-foreground/5 text-xs font-black uppercase tracking-tight" 
              />
            </div>
          </div>
          
          <div className="space-y-4 flex-1 overflow-y-auto pr-3 custom-scrollbar">
            {notasFiltradas.map((n) => (
              <NotaAuditRow key={n.chave} nota={n} regraFiltro={filtroRegra} />
            ))}
            {notasFiltradas.length === 0 && (
              <div className="flex flex-col items-center justify-center h-full text-muted-foreground opacity-10 animate-pulse">
                <ShieldCheck className="size-24 mb-6" />
                <span className="text-lg font-black uppercase tracking-[0.5em]">System Secure</span>
              </div>
            )}
          </div>
          
          <div className="mt-10 pt-10 border-t border-foreground/[0.03] grid md:grid-cols-2 gap-8 shrink-0">
            <div className="flex items-center gap-5 p-6 rounded-3xl glass-technical border-foreground/5 group hover:border-primary/20 transition-all bg-white/[0.01]">
              <div className="size-14 rounded-2xl bg-primary/10 text-primary flex items-center justify-center group-hover:scale-110 transition-transform duration-500 shadow-xl"><Clock className="size-7" /></div>
              <div>
                <div className="text-[10px] font-black uppercase tracking-widest text-muted-foreground opacity-60">Engine Runtime</div>
                <div className="text-2xl font-black tabular-nums tracking-tighter leading-none mt-1">{fmtDur(duracaoMs)}</div>
              </div>
            </div>
            <div className="flex items-center gap-5 p-6 rounded-3xl glass-technical border-foreground/5 group hover:border-emerald-500/20 transition-all bg-white/[0.01]">
              <div className="size-14 rounded-2xl bg-emerald-500/10 text-emerald-500 flex items-center justify-center group-hover:scale-110 transition-transform duration-500 shadow-xl"><Cpu className="size-7" /></div>
              <div>
                <div className="text-[10px] font-black uppercase tracking-widest text-muted-foreground opacity-60">System Stability</div>
                <div className="text-2xl font-black tabular-nums tracking-tighter leading-none mt-1">100% Stable</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function NotaAuditRow({ nota, regraFiltro }: any) {
  const [open, setOpen] = useState(false);
  const findings = nota.auditFindings ?? [];
  const visiveis = regraFiltro ? findings.filter((f: any) => f.regra === regraFiltro) : findings;
  const sevMax = nota.auditSeveridadeMax || "INFO";

  return (
    <div className={cn("rounded-3xl border-2 transition-all duration-500 glass-technical overflow-hidden group", open ? "border-primary/30 shadow-2xl bg-primary/[0.01]" : "border-foreground/[0.03] hover:border-primary/10")}>
      <button onClick={() => setOpen(!open)} className="w-full text-left p-6 flex items-center justify-between gap-6">
        <div className="flex items-center gap-6 min-w-0">
          <SeverityBadge s={sevMax} />
          <div className="min-w-0">
            <div className="text-lg font-black tracking-tighter group-hover:text-primary transition-colors leading-none mb-2 truncate">NF {nota.numero}/{nota.serie} <span className="opacity-30 mx-1">·</span> {nota.emitente}</div>
            <div className="flex items-center gap-4">
               <div className="text-[10px] font-black text-muted-foreground/60 tracking-tight flex items-center gap-1.5 uppercase">
                  <Database className="size-3" /> {nota.chave}
               </div>
               <div className="text-[9px] font-black text-emerald-500 uppercase tracking-widest flex items-center gap-1">
                  <CheckCircle2 className="size-3" /> VERIFIED
               </div>
            </div>
          </div>
        </div>
        <div className="flex items-center gap-4 shrink-0">
          <div className="flex flex-col items-end">
             <span className="text-[9px] font-black text-primary uppercase tracking-[0.2em]">{visiveis.length} FINDINGS</span>
          </div>
          <div className={cn("size-8 rounded-2xl flex items-center justify-center bg-muted/10 transition-all duration-500 group-hover:bg-primary group-hover:text-white", open && "rotate-90 bg-primary shadow-xl")}>
            <ArrowRight className="size-4" />
          </div>
        </div>
      </button>
      
      {open && (
        <div className="px-6 pb-6 space-y-4 animate-in slide-in-from-top-4 duration-500">
          <div className="h-px w-full bg-gradient-to-r from-transparent via-foreground/[0.05] to-transparent mb-6" />
          {visiveis.map((f: any, i: number) => (
            <div key={i} className="flex gap-6 p-6 rounded-[2rem] bg-white/[0.02] border-2 border-foreground/[0.03] group/item hover:border-primary/20 transition-all">
              <div className="shrink-0"><SeverityBadge s={f.severidade} /></div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-3 mb-2">
                   <span className="font-black text-primary uppercase bg-primary/10 px-3 py-1.5 rounded-xl text-[10px] tracking-widest">{f.regra}</span>
                   <span className="text-[9px] font-black text-muted-foreground uppercase tracking-widest opacity-40">Rule Applied</span>
                </div>
                <p className="text-sm font-bold text-foreground/80 leading-relaxed uppercase tracking-tight">{f.mensagem}</p>
                {f.campo && (
                  <div className="text-[10px] font-black text-muted-foreground uppercase tracking-widest mt-6 pt-4 border-t border-foreground/[0.03] flex items-center gap-2">
                    <Database className="size-3 text-primary" /> TAG_REFERENCE: <span className="text-primary font-black italic">{f.campo}</span>
                  </div>
                )}
              </div>
            </div>
          ))}
          <div className="pt-6 mt-4 flex items-center justify-between text-[10px] font-black text-muted-foreground uppercase tracking-widest opacity-40 border-t border-foreground/[0.03]">
            <div className="flex items-center gap-3">
              <Fingerprint className="size-4 text-sky-500" /> HASH SHA-256: {nota.xmlHash}
            </div>
            <div className="flex items-center gap-2">
               FILE_SIZE: {fmtNum(nota.xmlBytes || 0)} BYTES
            </div>
          </div>
        </div>
      )}
    </div>
  );
}


